import * as React from 'react';
import { connect } from 'react-redux';
import { Button, Card, Col, Layout, Row } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import {
	getCurrency,
	nullSafeGet,
	nullSafeGetOrElse,
	nullSafeGetWithZeroOrElse,
} from '../../utils/DataAccessUtils';
import { analyticsNow, formatDateForParam } from '../../utils/DataFormatterUtils';
import { supplierPerformanceFiltersToApiParams } from '../kpi_analytics_overview_supplier_performance_page/SupplierPerformanceUtils';
import AnalyticsControlBar from '../analytics_control_bar/AnalyticsControlBar';
import {
	SCORECARD_MONTHLY_ENDPOINTS,
	fetchSupplierScorecardComparison,
	fetchSupplierScorecardMonthly,
	fetchSupplierScorecardSummary,
} from '../../thunks/supplier_scorecards_thunks';
import KPIMetricChart from './KPIMetricChart';
import KPIWorkOrdersChart from './KPIWorkOrdersChart';
import KPISpendChart from './KPISpendChart';
import { withRouter } from 'react-router';
import {
	supplierFacilitiesRestCrudThunksForBuyer,
	supplierFacilitiesRestCrudThunksForSupplier,
} from '../../thunks/supplier_facilities_thunks';
import { clearKPIVendorFilters, updateKPIVendorFilters } from '../../actions/kpi_analytics_actions';
import { parseSafeFloat } from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsUtils';
import { debounce } from '../../utils/PerformanceUtils';
import { ANALYTICS_DEFAULT_TIME_ZONE, ROLE_TYPES } from '../../utils/DataConstants';
import { Link } from 'react-router-dom';
import { generateBackLink, getBackLinkStates } from '../../utils/HistoryUtils';
import { clearNavbarState, updateNavbarState } from '../../actions/navbar_actions';
import { exportAnalyticsPageAsPdf } from '../../utils/AnalyticsPdfExportUtils';

const queryString = require('qs');

const style = require('./KPIAnalyticsVendorDetailPage.less');
const isEqual = require('fast-deep-equal');

const Content = Layout.Content;

const IDENTITY = (row) => row;

const buildScorecardParams = (kpiVendorFilters, supplierFacilityId) => {
	const filters = kpiVendorFilters || {};
	const params = supplierPerformanceFiltersToApiParams(filters);
	params.supplierFacilityIds = String(supplierFacilityId);
	return params;
};

const buildScorecardChartFilters = (kpiVendorFilters, supplierFacilityId) => {
	const window = kpiVendorFilters && kpiVendorFilters.startDate && kpiVendorFilters.endDate;
	const params = buildScorecardParams(kpiVendorFilters, supplierFacilityId);
	delete params.completedAtStartDate;
	delete params.completedAtEndDate;
	return {
		...params,
		startDate: window ? formatDateForParam(kpiVendorFilters.startDate) : undefined,
		endDate: window ? formatDateForParam(kpiVendorFilters.endDate) : undefined,
	};
};

const currentRows = (rows, mapRow) =>
	(rows || []).filter((row) => !row.isPreviousPeriod && !row.isPreviousYear).map(mapRow);

const mapWorkOrderRow = (row) => ({
	...row,
	totalWOs: nullSafeGetOrElse('workOrderCount', row, 0),
	averageWOCost: nullSafeGetOrElse('avgCost', row, 0),
});

const mapSpendRow = (row) => ({
	...row,
	monthlyTotal:
		row && row.monthlyTotal !== null && row.monthlyTotal !== undefined
			? row.monthlyTotal
			: nullSafeGetWithZeroOrElse('monthlyAverage', row, 0),
});

const scaleRateRow = (divisor) => (row) => {
	const raw = nullSafeGet('monthlyAverage', row);
	if (raw === null || raw === undefined) return row;
	return { ...row, monthlyAverage: parseSafeFloat(raw) / divisor };
};

const averageOfMonthlyAverage = (rows) => {
	const values = (rows || [])
		.map((row) => parseSafeFloat(nullSafeGet('monthlyAverage', row), NaN))
		.filter((num) => !isNaN(num));
	if (values.length === 0) return null;
	return values.reduce((sum, num) => sum + num, 0) / values.length;
};

const buildWoCountByMonthKey = (workOrderRows) => {
	const counts = {};
	(workOrderRows || []).forEach((row) => {
		if (row && row.yearMonthKey) {
			counts[row.yearMonthKey] = parseSafeFloat(row.totalWOs, 0);
		}
	});
	return counts;
};

const volumeWeightedMonthlyAverage = (rows, woCountByMonthKey) => {
	let weightedSum = 0;
	let weightTotal = 0;
	(rows || []).forEach((row) => {
		const value = parseSafeFloat(nullSafeGet('monthlyAverage', row), NaN);
		const count = parseSafeFloat((woCountByMonthKey || {})[row && row.yearMonthKey], 0);
		if (!isNaN(value) && count > 0) {
			weightedSum += value * count;
			weightTotal += count;
		}
	});
	return weightTotal > 0 ? weightedSum / weightTotal : averageOfMonthlyAverage(rows);
};

const sumWorkOrders = (rows) =>
	(rows || []).reduce((sum, row) => sum + (parseSafeFloat(row.totalWOs, 0) || 0), 0);

const weightedAverageWOCost = (rows) => {
	let costTotal = 0;
	let woTotal = 0;
	(rows || []).forEach((row) => {
		const cost = parseSafeFloat(row.averageWOCost, NaN);
		const count = parseSafeFloat(row.totalWOs, 0);
		if (!isNaN(cost) && count > 0) {
			costTotal += cost * count;
			woTotal += count;
		}
	});
	return woTotal > 0 ? costTotal / woTotal : 0;
};

class KPIAnalyticsVendorDetailPage extends React.Component<any, any> {
	state = {
		scorecardLoading: true,
		scorecardData: {
			summary: null,
			workOrders: [],
			responseTime: [],
			resolutionTime: [],
			firstTimeFix: [],
			spend: [],
			checkIn: [],
			completedOnTime: [],
			invoicedWithin30: [],
		},
	};

	scorecardRequestId = 0;

	debouncedCallback = debounce(
		() => {
			const { kpiAnalytics, fetchScorecardSummary, fetchScorecardMonthly, match } = this.props;
			const { kpiVendorFilters } = kpiAnalytics;
			const params = buildScorecardParams(kpiVendorFilters, parseInt(match.params.id, 10));
			const requestId = this.scorecardRequestId + 1;
			this.scorecardRequestId = requestId;
			this.setState({ scorecardLoading: true });

			Promise.all([
				fetchScorecardSummary(params),
				fetchScorecardMonthly(SCORECARD_MONTHLY_ENDPOINTS.workOrders, params),
				fetchScorecardMonthly(SCORECARD_MONTHLY_ENDPOINTS.responseTime, params),
				fetchScorecardMonthly(SCORECARD_MONTHLY_ENDPOINTS.resolutionTime, params),
				fetchScorecardMonthly(SCORECARD_MONTHLY_ENDPOINTS.firstTimeFix, params),
				fetchScorecardMonthly(SCORECARD_MONTHLY_ENDPOINTS.spend, params),
				fetchScorecardMonthly(SCORECARD_MONTHLY_ENDPOINTS.checkIn, params),
				fetchScorecardMonthly(SCORECARD_MONTHLY_ENDPOINTS.completedOnTime, params),
				fetchScorecardMonthly(SCORECARD_MONTHLY_ENDPOINTS.invoicedWithin30Days, params),
			]).then(
				([
					summary,
					workOrders,
					responseTime,
					resolutionTime,
					firstTimeFix,
					spend,
					checkIn,
					completedOnTime,
					invoicedWithin30,
				]) => {
					if (this.scorecardRequestId !== requestId) return;
					this.setState({
						scorecardLoading: false,
						scorecardData: {
							summary,
							workOrders: currentRows(workOrders, mapWorkOrderRow),
							responseTime: currentRows(responseTime, IDENTITY),
							resolutionTime: currentRows(resolutionTime, IDENTITY),
							firstTimeFix: currentRows(firstTimeFix, scaleRateRow(100)),
							spend: currentRows(spend, mapSpendRow),
							checkIn: currentRows(checkIn, IDENTITY),
							completedOnTime: currentRows(completedOnTime, IDENTITY),
							invoicedWithin30: currentRows(invoicedWithin30, IDENTITY),
						},
					});
				}
			);
		},
		500,
		false
	);

	handlePrint = () =>
		exportAnalyticsPageAsPdf(
			'.kpiAnalyticsVendorDetailPage',
			'kpi-vendor-detail-' + new Date().toISOString().slice(0, 10) + '.pdf'
		);

	makeComparisonFetch = (endpoint, mapRow) => (params) =>
		this.props.fetchScorecardComparison(endpoint, params).then((rows) => (rows || []).map(mapRow));

	componentDidUpdate(prevProps) {
		const { kpiAnalytics } = this.props;
		if (!isEqual(prevProps.kpiAnalytics.kpiVendorFilters, kpiAnalytics.kpiVendorFilters)) {
			this.debouncedCallback();
		}
	}

	componentDidMount() {
		const { fetchSupplierFacility, match, saveKPIVendorFilters, location, updateNavbar } =
			this.props;
		const supplierFacilityId = match.params.id;
		fetchSupplierFacility(supplierFacilityId);

		let searchString = location.search;
		if (searchString[0] === '?') {
			searchString = searchString.slice(1);
		}
		const queryParams = queryString.parse(searchString);
		saveKPIVendorFilters({
			locationIds: queryParams.locationIds,
			problemTypeIds: queryParams.problemTypeIds,
			woPriorityIds: queryParams.woPriorityIds,
			isPM: queryParams.isPM,
			startDate: analyticsNow(ANALYTICS_DEFAULT_TIME_ZONE).subtract(6, 'months'),
			endDate: analyticsNow(ANALYTICS_DEFAULT_TIME_ZONE),
		});
		const backLinkStates = getBackLinkStates(
			location,
			`/buyer/analytics/kpi/overview/vendors`
		) as any;
		updateNavbar(
			backLinkStates.backLinkText || `Back to Vendors`,
			backLinkStates.backLinkTo ||
				generateBackLink(`/buyer/analytics/kpi/overview/vendors`, location)
		);
	}

	componentWillUnmount() {
		const { clearAllKPIVendorFilters, clearNavbar } = this.props;
		clearAllKPIVendorFilters();
		clearNavbar();
	}

	memoizedChartFilters: Record<string, any> = null;

	getChartFilters(kpiVendorFilters, supplierFacilityId) {
		const nextFilters = buildScorecardChartFilters(kpiVendorFilters, supplierFacilityId);
		if (this.memoizedChartFilters && isEqual(this.memoizedChartFilters, nextFilters)) {
			return this.memoizedChartFilters;
		}
		this.memoizedChartFilters = nextFilters;
		return nextFilters;
	}

	render() {
		const { supplierFacility, companyConfig, match, kpiAnalytics, currentUser, userType } =
			this.props;
		const { scorecardLoading, scorecardData } = this.state;
		const appliedFilters = kpiAnalytics.kpiVendorFilters || {};
		const currency = getCurrency({ currentUser });
		const supplierFacilityId = parseInt(match.params.id, 10);
		const summary = scorecardData.summary;
		const woCountByMonthKey = buildWoCountByMonthKey(scorecardData.workOrders);
		const chartFilters = this.getChartFilters(kpiAnalytics.kpiVendorFilters, supplierFacilityId);

		const metricCards: Record<string, any>[] = [
			{ type: 'workOrders', chartKey: 'work_orders' },
			{
				type: 'metric',
				name: 'Avg Response Time',
				chartKey: 'time_to_accept',
				metric: scorecardData.responseTime,
				isTimeBased: true,
				endpoint: SCORECARD_MONTHLY_ENDPOINTS.responseTime,
				mapRow: IDENTITY,
			},
			{
				type: 'metric',
				name: 'Avg Resolution Time',
				chartKey: 'resolution_time',
				metric: scorecardData.resolutionTime,
				isTimeBased: true,
				endpoint: SCORECARD_MONTHLY_ENDPOINTS.resolutionTime,
				mapRow: IDENTITY,
			},
			{
				type: 'metric',
				name: 'First Time Fix Rate',
				chartKey: 'first_time_resolution',
				metric: scorecardData.firstTimeFix,
				isTimeBased: false,
				endpoint: SCORECARD_MONTHLY_ENDPOINTS.firstTimeFix,
				mapRow: scaleRateRow(100),
			},
			{ type: 'spend', chartKey: 'spend' },
			{
				type: 'metric',
				name: 'Checked In / Out',
				chartKey: 'check_in_compliance',
				metric: scorecardData.checkIn,
				isTimeBased: false,
				endpoint: SCORECARD_MONTHLY_ENDPOINTS.checkIn,
				mapRow: IDENTITY,
			},
			{
				type: 'metric',
				name: 'Completed On Time',
				chartKey: 'timely_work_completion',
				metric: scorecardData.completedOnTime,
				isTimeBased: false,
				endpoint: SCORECARD_MONTHLY_ENDPOINTS.completedOnTime,
				mapRow: IDENTITY,
			},
			{
				type: 'metric',
				name: 'Invoiced Within 30 Days',
				chartKey: 'timely_invoicing',
				metric: scorecardData.invoicedWithin30,
				isTimeBased: false,
				endpoint: SCORECARD_MONTHLY_ENDPOINTS.invoicedWithin30Days,
				mapRow: IDENTITY,
			},
		];

		return (
			<Content className="kpiAnalyticsVendorDetailPage" style={{ padding: '0 0.5em' }}>
				<div
					style={{
						margin: '0 -8px',
						padding: '24px',
						paddingBottom: 0,
						background: nullSafeGet('detail.config.theme.navblock', companyConfig),
						marginBottom: 8,
					}}
				>
					<h4
						style={{
							color: 'rgba(255, 255, 255, 0.9)',
							marginRight: 8,
							paddingBottom: 24,
						}}
					>
						{supplierFacility.displayName}
					</h4>
				</div>
				<Row style={{ margin: '16px 4px' }} gutter={16}>
					<Col span={24}>
						<div className="flex flex-wrap items-center gap-3">
							<AnalyticsControlBar
								userType={userType}
								updateFilters={this.props.saveKPIVendorFilters}
								startDate={appliedFilters.startDate}
								endDate={appliedFilters.endDate}
								dateRangeLabel="Completed Between"
								locationIds={appliedFilters.locationIds}
								problemTypeIds={appliedFilters.problemTypeIds}
								woPriorityIds={appliedFilters.woPriorityIds}
								isPM={appliedFilters.isPM}
								workCreatedAtStartDate={appliedFilters.workCreatedAtStartDate}
								workCreatedAtEndDate={appliedFilters.workCreatedAtEndDate}
								filterConfig={{
									problemTypes: true,
									workOrderPriorities: true,
									isPM: true,
									workCreatedAt: true,
									isSupplierAnalytics: true,
								}}
								hideLocationsControl={false}
								hideSuppliersControl={true}
								showSearch={false}
								showDownload={false}
								showPrint={true}
								onPrint={this.handlePrint}
							/>
						</div>
					</Col>
				</Row>

				<Row style={{ margin: '16px 0px 0px 0px' }} gutter={16}>
					<Col span={12}>
						<Card
							loading={scorecardLoading && !summary}
							title="completed work orders"
							actions={[
								<Link
									target="_blank"
									to={`/${userType}/workOrders/overview/all?current=1&pageSize=5&sort_by=createdAt&order=desc&displayStatuses=Completed&supplierFacilityIds=${supplierFacilityId}`}
								>
									<Button type="link">
										See details <i className="icons8-font icons8-external-link ml-2 inline-block" />
									</Button>
								</Link>,
							]}
							extra={
								<div className="text-base text-gray-500">
									LIFETIME: {nullSafeGetWithZeroOrElse('completedLifetime', summary, 0)}
								</div>
							}
						>
							<div className="flex h-40 flex-col items-center justify-center">
								<div className="text-center text-8xl font-normal ">
									{nullSafeGetWithZeroOrElse('completedPeriod', summary, 0)}
								</div>
							</div>
						</Card>
					</Col>
					<Col span={12}>
						<Card
							loading={scorecardLoading && !summary}
							title="Open work orders"
							actions={[
								<Link
									target="_blank"
									to={`/${userType}/workOrders/overview/all?current=1&pageSize=5&sort_by=createdAt&order=desc&displayStatuses=Open%2CInProgress%2COnHold&supplierFacilityIds=${supplierFacilityId}`}
								>
									<Button type="link">
										See details <i className="icons8-font icons8-external-link ml-2 inline-block" />
									</Button>
								</Link>,
							]}
							extra={
								<div className="text-base text-gray-500">
									LIFETIME: {nullSafeGetWithZeroOrElse('openLifetime', summary, 0)}
								</div>
							}
						>
							<div className="flex h-40 flex-col items-center justify-center">
								<div className="text-center text-8xl font-normal ">
									{nullSafeGetWithZeroOrElse('openPeriod', summary, 0)}
								</div>
							</div>
						</Card>
					</Col>
				</Row>
				<Row style={{ margin: '16px 0px 0px 0px' }} gutter={16} align="stretch">
					{metricCards.map((card) => (
						<Col key={card.chartKey} span={12} style={{ marginBottom: 16 }}>
							{card.type === 'workOrders' ? (
								<KPIWorkOrdersChart
									data={scorecardData.workOrders}
									loading={scorecardLoading}
									filters={chartFilters}
									fetchComparisonData={this.makeComparisonFetch(
										SCORECARD_MONTHLY_ENDPOINTS.workOrders,
										mapWorkOrderRow
									)}
									userType={userType}
									currency={currency}
									totalWOs={sumWorkOrders(scorecardData.workOrders)}
									averageWOCost={weightedAverageWOCost(scorecardData.workOrders)}
								/>
							) : card.type === 'spend' ? (
								<KPISpendChart
									data={scorecardData.spend}
									loading={scorecardLoading}
									filters={chartFilters}
									fetchComparisonData={this.makeComparisonFetch(
										SCORECARD_MONTHLY_ENDPOINTS.spend,
										mapSpendRow
									)}
									userType={userType}
									currency={currency}
								/>
							) : (
								<KPIMetricChart
									chartKey={card.chartKey}
									label={card.name}
									summaryValue={volumeWeightedMonthlyAverage(card.metric, woCountByMonthKey)}
									data={card.metric}
									loading={scorecardLoading}
									isTimeBased={card.isTimeBased}
									fetchComparisonData={this.makeComparisonFetch(card.endpoint, card.mapRow)}
									showAverageReferenceLine
									filters={chartFilters}
									userType={userType}
								/>
							)}
						</Col>
					))}
				</Row>
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	supplierFacilitiesFetching: state.supplier_facilities.fetching,
	supplierFacility: state.supplier_facilities.detail,
	companyConfig: state.company_config,
	match: ownProps.match,
	history: ownProps.history,
	kpiAnalytics: state.kpi_analytics,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchSupplierFacility: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? supplierFacilitiesRestCrudThunksForSupplier.readOne(id)
				: supplierFacilitiesRestCrudThunksForBuyer.readOne(id)
		),
	saveKPIVendorFilters: (filters) => dispatch(updateKPIVendorFilters(filters)),
	clearAllKPIVendorFilters: () => dispatch(clearKPIVendorFilters()),
	fetchScorecardSummary: (params) => fetchSupplierScorecardSummary(ownProps.userType)(params),
	fetchScorecardMonthly: (endpoint, params) =>
		fetchSupplierScorecardMonthly(ownProps.userType)(endpoint)(params),
	fetchScorecardComparison: (endpoint, params) =>
		fetchSupplierScorecardComparison(endpoint)(ownProps.userType)(params),
	updateNavbar: (backlinkTitle, backlinkTo, navbarBackgroundColor) =>
		dispatch(updateNavbarState(backlinkTitle, backlinkTo, navbarBackgroundColor)),
	clearNavbar: () => dispatch(clearNavbarState()),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(KPIAnalyticsVendorDetailPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
